import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "tab",
    "channelInput",
    "subjectField",
    "subject",
    "submit",
    "form",
    "body",
    "bodyWrap",
    "notice",
    "aiMenu",
    "aiMenuIcon",
    "draftStatus",
    "draftStatusLabel",
    "draftShimmer",
    "whatsappSetup"
  ]
  static values = {
    labels: Object,
    aiDraftUrl: String,
    i18n: Object,
    smsAvailable: Boolean,
    whatsappAvailable: Boolean,
    whatsappSetupRequired: Boolean,
    defaultChannel: String
  }

  connect() {
    const channel = this.hasDefaultChannelValue ? this.defaultChannelValue : "email"
    this.setChannel(channel)
    this.bindTemplateButtons()
    this.draftRequest = null
  }

  disconnect() {
    this.draftRequest?.abort()
  }

  selectTab(event) {
    event.preventDefault()
    this.setChannel(event.currentTarget.dataset.channel)
  }

  setChannel(channel) {
    if (this.hasChannelInputTarget) {
      this.channelInputTarget.value = channel
    }

    this.tabTargets.forEach((tab) => {
      const active = tab.dataset.channel === channel
      tab.dataset.active = active ? "true" : "false"
      tab.setAttribute("aria-selected", active ? "true" : "false")
    })

    if (this.hasSubjectFieldTarget) {
      this.subjectFieldTarget.hidden = channel !== "email"
    }

    const copy = this.labelsValue[channel] || this.labelsValue.email
    if (this.hasSubmitTarget) {
      this.submitTarget.value = copy.submit
    }

    this.syncTurbo(channel)
    this.syncWhatsappSetup(channel)
  }

  syncWhatsappSetup(channel) {
    if (!this.hasWhatsappSetupTarget) return

    const show = channel === "whatsapp" && this.whatsappSetupRequiredValue && !this.whatsappAvailableValue
    this.whatsappSetupTarget.hidden = !show
    this.whatsappSetupTarget.classList.toggle("hidden", !show)
  }

  syncTurbo(channel) {
    if (!this.hasFormTarget) return

    const nativeSend =
      channel === "email" ||
      channel === "messenger" ||
      channel === "instagram" ||
      (channel === "sms" && this.smsAvailableValue) ||
      (channel === "whatsapp" && this.whatsappAvailableValue)

    // External fallbacks (wa.me, sms:, tel:) must not use Turbo fetch — cross-origin redirects fail CORS.
    this.formTarget.dataset.turbo = nativeSend ? "true" : "false"
  }

  async draftWithAi(event) {
    event.preventDefault()
    if (!this.hasAiDraftUrlValue || this.draftLoading) return

    this.closeAiMenu()
    this.draftRequest?.abort()
    this.draftRequest = new AbortController()
    this.setDraftLoading(true)
    this.showNotice("", null)

    try {
      const response = await fetch(this.aiDraftUrlValue, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken,
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin",
        signal: this.draftRequest.signal
      })

      if (!response.ok) {
        throw new Error("draft failed")
      }

      const payload = await response.json()
      this.applyDraft(payload)
      this.showNotice(
        payload.notice_kind === "ai" ? this.i18nValue.draft_from_ai : this.i18nValue.draft_from_template,
        payload.notice_kind === "ai" ? "ai" : "template"
      )
      if (this.hasBodyTarget) {
        this.bodyTarget.focus()
      }
    } catch (error) {
      if (error.name === "AbortError") return
      this.showNotice(this.i18nValue.draft_failed, "error")
    } finally {
      this.setDraftLoading(false)
      this.draftRequest = null
    }
  }

  applyDraft(payload) {
    if (this.hasSubjectTarget && payload.subject) {
      this.subjectTarget.value = payload.subject
    }
    if (this.hasBodyTarget && payload.body) {
      this.bodyTarget.value = payload.body
    }
    this.setChannel("email")
  }

  showNotice(message, kind) {
    if (!this.hasNoticeTarget) return
    if (!message) {
      this.noticeTarget.innerHTML = ""
      this.noticeTarget.classList.add("empty:hidden")
      return
    }

    const className =
      kind === "ai"
        ? "pro-inbox-compose-notice pro-inbox-compose-notice--ai"
        : kind === "template"
          ? "pro-inbox-compose-notice pro-inbox-compose-notice--template"
          : kind === "success"
            ? "pro-inbox-compose-notice pro-inbox-compose-notice--success"
            : "pro-inbox-compose-notice pro-inbox-compose-notice--error"

    const icon =
      kind === "ai"
        ? `<svg class="pro-ai-icon pro-ai-icon--sm inline-block align-[-0.15em] mr-1" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M10 1.75 11.2 7.5 17 8.75 11.2 10 10 15.75 8.8 10 3 8.75 8.8 7.5 10 1.75Z" fill="currentColor"/><path d="M15.75 2.5 16.15 4.35 18 4.75 16.15 5.15 15.75 7 15.35 5.15 13.5 4.75 15.35 4.35 15.75 2.5Z" fill="currentColor" opacity="0.72"/></svg>`
        : ""

    this.noticeTarget.innerHTML = `<p class="${className}" role="status">${icon}<span></span></p>`
    this.noticeTarget.querySelector("span").textContent = message
  }

  setDraftLoading(loading) {
    this.draftLoading = loading
    this.element.classList.toggle("pro-inbox-compose--drafting", loading)

    if (this.hasDraftStatusTarget) {
      this.draftStatusTarget.classList.toggle("hidden", !loading)
      this.draftStatusTarget.hidden = !loading
      this.draftStatusTarget.setAttribute("aria-busy", loading ? "true" : "false")
      if (this.hasDraftStatusLabelTarget) {
        this.draftStatusLabelTarget.textContent = loading ? this.i18nValue.drafting : ""
      }
    }

    if (this.hasDraftShimmerTarget) {
      this.draftShimmerTarget.classList.toggle("hidden", !loading)
    }

    if (this.hasAiMenuIconTarget) {
      this.aiMenuIconTarget.classList.toggle("pro-ai-icon--busy", loading)
    }

    if (this.hasSubjectTarget) {
      this.subjectTarget.readOnly = loading
    }

    if (this.hasBodyTarget) {
      this.bodyTarget.readOnly = loading
      if (loading) {
        this.bodyTarget.setAttribute("aria-busy", "true")
      } else {
        this.bodyTarget.removeAttribute("aria-busy")
      }
    }

    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = loading
    }
  }

  closeAiMenu() {
    if (this.hasAiMenuTarget) {
      this.aiMenuTarget.removeAttribute("open")
    }
  }

  submitStarted() {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true
      this.submitTarget.setAttribute("aria-busy", "true")
    }
  }

  submitEnded(event) {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = false
      this.submitTarget.removeAttribute("aria-busy")
    }

    if (event.detail.success && this.hasBodyTarget && this.channelInputTarget?.value === "email") {
      window.setTimeout(() => this.showNotice("", null), 4000)
    }
  }

  bindTemplateButtons() {
    this.element.querySelectorAll("[data-compose-target='inquiry-reply-body']").forEach((button) => {
      button.addEventListener("click", () => {
        if (this.hasBodyTarget && button.dataset.templateBody) {
          this.bodyTarget.value = button.dataset.templateBody
          this.bodyTarget.focus()
          button.closest("details")?.removeAttribute("open")
        }
      })
    })
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
